/**
 * Конфиг нового слоя режима «Навигация» (docs/wayfinding/layers.md §2.1). Один на всех, кто
 * слой создаёт: кнопка «+» в панели режима, инструменты «Указатель» и «Узел», агент MCP
 * (docs/mcp/wayfinding.md, G2). Раньше его собирал только веб, и слой, заведённый мимо веба,
 * рождался бы без схемы полей объектов и без блока, которым его рисует карта.
 */
import type { CustomPropertyDef } from '../types/layer.js';
import {
  WAYFINDING_LAYER_FLAG,
  WAYFINDING_LAYER_KIND,
  WAYFINDING_LAYER_SIGN_TYPE,
  type WayfindingLayerKind,
} from '../types/wayfinding.js';
import { DEFAULT_FEATURE_NAME_CUSTOM_PROPERTY } from './ensureNameCustomProperty.js';
import { createDefaultVizBlock } from './vizBlockEditing.js';
import { withWayfindingObjectFields } from './wayfindingObjectFields.js';

/**
 * Цвет графа: узлы и рёбра одного цвета — иначе граф распадается на «точки» и «линии» и
 * перестаёт читаться как одна сеть.
 */
export const WAYFINDING_GRAPH_HEX = '#f97316';

export interface WayfindingLayerConfig {
  name: string;
  type: 'vector';
  sourceConfig: Record<string, unknown>;
  styleConfig: Record<string, unknown>;
}

/**
 * @param kind — вид слоя: носитель указателей или объекты графа.
 * @param name — название слоя (подпись по умолчанию на языке интерфейса — забота вызывающего).
 * @param signTypeId — тип носителя слоя указателей (`WAYFINDING_LAYER_SIGN_TYPE`), только у `signs`.
 */
export function createWayfindingLayerConfig(
  kind: WayfindingLayerKind,
  name: string,
  signTypeId?: string,
): WayfindingLayerConfig {
  const baseProperties: CustomPropertyDef[] = [DEFAULT_FEATURE_NAME_CUSTOM_PROPERTY];
  return {
    name,
    type: 'vector',
    sourceConfig: {
      // Название и тип помещения — поля схемы с рождения: их сразу видят таблица по группе,
      // фильтры и раскраска (docs/wayfinding/layers.md §3.7).
      customProperties:
        kind === 'objects' ? (withWayfindingObjectFields(baseProperties) ?? baseProperties) : baseProperties,
      [WAYFINDING_LAYER_FLAG]: true,
      [WAYFINDING_LAYER_KIND]: kind,
      ...(kind === 'signs' && signTypeId ? { [WAYFINDING_LAYER_SIGN_TYPE]: signTypeId } : {}),
    },
    styleConfig: {
      /**
       * «Объекты» рождаются с точками в цвете графа: узел — точка по определению, а ждать
       * авто-блока нельзя — узлы пишутся ручками режима в обход рисования и иначе ложились бы
       * в базу, не появляясь на карте. «Указатели» — с блоком «Указатели» (✏️ 13.09): знак
       * типа, превью и подпись рисует он. Флаг авто-блока остаётся: линия или полигон,
       * нарисованные в слой, заведут себе блок сами, как в любом слое.
       */
      visualizations: [
        kind === 'objects' ? createDefaultVizBlock('circle', WAYFINDING_GRAPH_HEX) : createDefaultVizBlock('sign'),
      ],
      autoVisualization: true,
    },
  };
}
